// lib/leasing/campaignRow.ts
//
// ONE CAMPAIGN, READ IN ONE PLACE.
//
// A campaign is one row of `Campaign`. It is also served, for now, under a second name -
// `SourcingCampaign` - by the older route, and the client keeps a registry per name. Two registries
// holding the same row is the shape that produced every schema-legacy bug of 2026-08-18: whichever
// one a call site happened to read decided what the app believed, and a miss was indistinguishable
// from "no such campaign". Twenty-five call sites each made that choice on their own; some read one
// registry, some the other, some both in a different order.
//
// So the choice is made once, here. Call sites ask for a campaign, not for a registry.
//
// THE ORDER IS DELIBERATE: the unified row first, because it is the one the writers write and the
// only one carrying `targetKind`, `propertyIds` and `config.name` at every granularity. The legacy
// name is a fallback for the states where only it has been hydrated - a launch response, a cold
// entity page - and it is meant to disappear: when the client hydrates campaigns into `Campaign`
// everywhere, delete the second lookup and this file becomes a one-liner.

/** The shape every caller actually reads off a campaign. Deliberately loose: the two payloads have
 *  slightly different fields and a strict type here would force casts at every call site, which is
 *  what this module exists to remove. */
export interface CampaignRowLike {
  id?: string;
  status?: string | null;
  createdAt?: string;
  createdByUserId?: string | null;
  targetKind?: string | null;
  portfolioId?: string | null;
  /** The unified row's coverage. An asset run has one entry, a multi-asset run several. */
  propertyIds?: string[] | null;
  /** The legacy row's single asset. Read `resolveCampaignPropertyId` rather than either field. */
  propertyId?: string | null;
  config?: Record<string, unknown> | null;
  metrics?: unknown;
}

/** The two registries a campaign can be found in, in the order they are trusted. */
interface CampaignInventoryLike {
  Campaign?: { registry?: Record<string, unknown> };
  SourcingCampaign?: { registry?: Record<string, unknown> };
}

/**
 * The campaign with this id, wherever it has been hydrated, or undefined.
 *
 * Undefined means "not loaded", never "does not exist": the caller that needs certainty syncs it.
 */
export function readCampaignRow(
  inventory: CampaignInventoryLike | null | undefined,
  campaignId: string | null | undefined,
): CampaignRowLike | undefined {
  const id = typeof campaignId === 'string' ? campaignId.trim() : '';
  if (!id || !inventory) return undefined;
  const unified = inventory.Campaign?.registry?.[id];
  if (unified) return unified as CampaignRowLike;
  return (inventory.SourcingCampaign?.registry?.[id] as CampaignRowLike | undefined) ?? undefined;
}

/**
 * The asset a campaign is about.
 *
 * The unified row carries an ARRAY, because one run can cover several assets; the legacy row
 * carries a single id. Reading the wrong one is how a multi-asset run reported the wrong asset -
 * the primary is the first of the coverage, in the order the user picked them.
 */
export function resolveCampaignPropertyId(row: CampaignRowLike | undefined): string {
  if (!row) return '';
  const first = Array.isArray(row.propertyIds) ? row.propertyIds[0] : undefined;
  if (typeof first === 'string' && first.trim().length > 0) return first.trim();
  return typeof row.propertyId === 'string' ? row.propertyId.trim() : '';
}
